import React, { useState } from "react";
import { useParams } from "react-router-dom";

import {
  Button,
  Dialog,
  FileInstance,
  FileUploadAndParse,
  Headline,
  Paragraph,
} from "@fronterahealth/frontera-ui-components";

import { FileTypeEnum } from "@api/graphql/types-and-hooks";
import {
  useGetFileParseStatusRetriever,
  useGetRemoveFileCallback,
  useGetReportFileUploadCompleteCallback,
  useGetS3BucketUploadCallback,
  useGetS3BucketUrlRetriever,
} from "@hooks/useLearnerFileUpload";

interface UploadTreatmentPlanProps {
  setUploadedFiles: (files: FileInstance[] | null) => void;
}

export const UploadTreatmentPlan: React.FC<UploadTreatmentPlanProps> = ({ setUploadedFiles }) => {
  const { clientId } = useParams();
  const S3BucketUrlRetriever = useGetS3BucketUrlRetriever({ learnerId: clientId ?? "<missing-learner-id>" });
  const reportFileUploadCompleteCallback = useGetReportFileUploadCompleteCallback();
  const S3BucketUploadCallback = useGetS3BucketUploadCallback();
  const fileParseStatusRetriever = useGetFileParseStatusRetriever();
  const removeFileCallback = useGetRemoveFileCallback();

  const [isLearnMoreDialogOpen, setIsLearnMoreDialogOpen] = useState<boolean>(false);

  return (
    <div className="flex flex-col gap-4">
      <Paragraph colorType="secondary">
        We accept images, or documents — even quick screenshots work. Once uploaded, we’ll scan for goals, programs, and
        targets to personalize your session insights.{" "}
        <Button
          appearance="text"
          style={{ padding: "2px" }}
          text="Learn More"
          onClick={() => setIsLearnMoreDialogOpen(true)}
        />
      </Paragraph>
      <FileUploadAndParse
        fileKind={FileTypeEnum.TreatmentPlan}
        title="Upload treatment plan(s)"
        supportedFileTypes={["pdf", "png", "jpeg", "jpg", "doc", "docx"]}
        maxSize={"1GB"}
        S3BucketUrlRetriever={async ({ fileKind, fileName, mimeType, fileSize }) => {
          setUploadedFiles(null);
          return await S3BucketUrlRetriever({ fileKind, fileName, mimeType, fileSize });
        }}
        S3BucketUploadCallback={S3BucketUploadCallback}
        reportFileUploadCompleteCallback={reportFileUploadCompleteCallback}
        fileParseStatusRetriever={fileParseStatusRetriever}
        removeFileCallback={removeFileCallback}
        updateFileStatusCallback={(files) => {
          const allDone = files.every((file) => file.fileStatus === "done");
          if (allDone) {
            setUploadedFiles(files);
          }
        }}
      />
      <Dialog
        title={
          <Headline size="extra-small" colorType="primary">
            Why adding your programming matters
          </Headline>
        }
        open={isLearnMoreDialogOpen}
        setOpen={setIsLearnMoreDialogOpen}
        primaryButton={undefined}
        secondaryButton={{
          text: "Okay, got it!",
          onClick: () => {
            setIsLearnMoreDialogOpen(false);
          },
        }}
        size="xl"
        overflowBehavior="scroll"
        key={Date.now()}
      >
        <div className="flex flex-col gap-4">
          <Paragraph colorType="primary">
            The goals and targets in your treatment plan are the foundation of meaningful insights.
          </Paragraph>
          <Paragraph colorType="primary">
            When you upload programming — even as a simple screenshot or document — we extract key details like goals,
            targets, and treatment structure. This information powers smarter analysis of your session videos. Here’s
            how it helps:
          </Paragraph>
          <div>
            <Paragraph displayType="loud" colorType="primary">
              • More Specific Session Summaries
            </Paragraph>
            <Paragraph colorType="primary">
              Instead of generic overviews, summaries can highlight moments directly tied to your client’s programming —
              like when specific targets were introduced, reinforced, or generalized.
            </Paragraph>

            <Paragraph displayType="loud" colorType="primary">
              • Tailored Insights
            </Paragraph>
            <Paragraph colorType="primary">
              We can surface progress indicators and implementation patterns that are unique to your client’s plan,
              giving you a clearer picture of what’s working.{" "}
            </Paragraph>
          </div>
          <Paragraph colorType="primary">
            Even partial uploads (like a screenshot of a session block or goal sheet) are enough to give our system the
            context it needs to begin tailoring your experience.
          </Paragraph>
        </div>
      </Dialog>
    </div>
  );
};
